class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class QuantityInputDawn extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input-dawn', QuantityInputDawn);

class ProductVariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {

      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');

      // change previous target.selectedOptions with new value
      const newSelectedOption = Array.from(target.options).find((option) => option.value === value);
      if (newSelectedOption) {
        newSelectedOption.setAttribute('selected', 'selected');
      }

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .product-option__swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('product-option__swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('product-option__swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    const optionValues = [];

    this.querySelectorAll("select").forEach((select) => {
      const selectedOption = select.selectedOptions?.[0];
      const optionValueId = selectedOption?.dataset?.optionValueId;
      if (optionValueId) optionValues.push(optionValueId);
    });

    this.querySelectorAll("fieldset").forEach((fieldset) => {
      const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
      const optionValueId = checkedInput?.dataset?.optionValueId;
      if (optionValueId) optionValues.push(optionValueId);
    });

    return optionValues;
  }
}
customElements.define('product-variant-selects', ProductVariantSelects);

if (!customElements.get('product-info')) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
        this.productMediaGallery = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        this.productVariantSelects = this.querySelector('product-variant-selects');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
        this.initUpdateMedia();
        this.ensureVariantInUrl();

        if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
          // Defer until Swiper instances are initialized
          const runVariantMedia = () => this.updateVariantMedia();
          const mainGallery = this.productMediaGallery.querySelector('swiper-product-gallery');
          if (mainGallery && mainGallery.instance) {
            runVariantMedia();
          } else if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => requestAnimationFrame(runVariantMedia));
          } else {
            requestAnimationFrame(runVariantMedia);
          }
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(
          ".product-form__quantity"
        );
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      initUpdateMedia() {
        if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') return;
        const variantIdFromUrl = parseInt(window.location.search.split('?').filter(item => item.includes('variant='))[0]?.split('=')[1]) || null;
        if (!variantIdFromUrl) { return; }
        const variant = this.getSelectedVariant(this);
        if (!variant || !variantIdFromUrl) { return; }
        if (variant?.id === variantIdFromUrl) {
          this.updateMedia(variant?.featured_media?.id);
        }
      }

      ensureVariantInUrl() {
        // On a direct PDP visit with no ?variant= in the address bar, reflect the
        // default/selected variant so the URL is shareable and matches card links.
        if (this.dataset.updateUrl !== "true") return;
        if (new URLSearchParams(window.location.search).has("variant")) return;
        const variant = this.getSelectedVariant(this);
        if (!variant?.id) return;
        this.updateURL(this.dataset.url, variant.id);
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push(html =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach(element =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push(newNode => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues }
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct =
          this.getProductPath(this.dataset.url) !== this.getProductPath(productUrl);
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;
        let selectedVariant = this.getVariantFromSelectedOptions(this);
        const isColorChange = this.isColorSelectionChange(event.target);

        // If the user changed Color but the current Size doesn't exist for that
        // color (selectedVariant=null), resolve to the FIRST AVAILABLE variant
        // of the clicked color. This makes the server render that color (not the
        // first-available fallback variant of a different color) and auto-picks
        // the only valid size for the new color.
        if (!selectedVariant && isColorChange && event.target?.value) {
          const allVariantsText = this.querySelector("[data-all-variants]")?.textContent;
          if (allVariantsText) {
            try {
              const allVariants = JSON.parse(allVariantsText);
              if (Array.isArray(allVariants) && allVariants.length) {
                const colorPos = this.getColorOptionPosition();
                const colorKey = `option${colorPos}`;
                const targetColor = String(event.target.value).trim();
                selectedVariant =
                  allVariants.find(v => String(v[colorKey] ?? '').trim() === targetColor && v.available)
                  || allVariants.find(v => String(v[colorKey] ?? '').trim() === targetColor);
              }
            } catch (e) { /* ignore */ }
          }
        }

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
            selectedVariant?.id
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, isColorChange)
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.submitButton?.removeAttribute("aria-disabled");
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return html => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(
            html.querySelector(selector)
          );
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then(response => response.text())
          .then(responseText => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch(error => {
            if (error.name === "AbortError") {
              // console.log("Fetch aborted by user");
            } else {
              const variant = this.getVariantFromSelectedOptions(this);
              const selectedSizeUnavailable = this.isSelectedSizeUnavailable(this);
              const shouldDisableSubmitButton = selectedSizeUnavailable || !variant?.available;
              this.productForm?.toggleSubmitButton(shouldDisableSubmitButton, window.variantStrings.soldOut);
              if (shouldDisableSubmitButton) {
                this.updateVariantInputs(null);
                this.syncStickyCartVariant(variant || null, true);
              } else {
                this.updateVariantInputs(variant?.id);
                this.productForm?.submitButton?.removeAttribute("aria-disabled");
                this.syncStickyCartVariant(variant, false);
              }
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const variantFromOptions = this.getVariantFromSelectedOptions(productInfoNode);
        if (variantFromOptions) return variantFromOptions;

        return this.getPayloadSelectedVariant(productInfoNode);
      }

      getPayloadSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "product-variant-selects [data-selected-variant]"
        )?.innerHTML;

        if (!selectedVariant) return null;
        try {
          const parsed = JSON.parse(selectedVariant);
          return parsed?.id ? parsed : null;
        } catch (e) {
          return null;
        }
      }

      getVariantFromSelectedOptions(productInfoNode) {
        const variantSelects = productInfoNode.querySelector("product-variant-selects");
        if (!variantSelects) return null;

        const allVariantsText = variantSelects.querySelector("[data-all-variants]")?.textContent;
        if (!allVariantsText) return null;

        let allVariants;
        try {
          allVariants = JSON.parse(allVariantsText);
        } catch (e) {
          return null;
        }
        if (!Array.isArray(allVariants) || !allVariants.length) return null;

        const selectedByPosition = this.getSelectedOptionsByPosition(variantSelects);
        const selectedPositions = Object.keys(selectedByPosition);

        let matchingVariants = [];
        if (selectedPositions.length) {
          matchingVariants = allVariants.filter((variant) =>
            selectedPositions.every((position) => {
              const optionValue = variant[`option${position}`];
              return String(optionValue ?? "").trim() === selectedByPosition[position];
            })
          );
        } else {
          const selectedOptions = this.getSelectedOptionsInOrder(variantSelects);
          if (!selectedOptions.length) return null;
          matchingVariants = allVariants.filter((variant) =>
            selectedOptions.every((value, index) => {
              const optionValue = variant[`option${index + 1}`];
              return String(optionValue ?? "").trim() === value;
            })
          );
        }

        if (!matchingVariants.length) return null;
        return matchingVariants[0];
      }

      getSelectedOptionsByPosition(variantSelects) {
        const selectedByPosition = {};

        variantSelects.querySelectorAll("fieldset").forEach((fieldset) => {
          const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
          if (!checkedInput?.value || !checkedInput.name) return;

          const positionMatch = checkedInput.name.match(/-(\d+)$/);
          if (!positionMatch) return;

          selectedByPosition[positionMatch[1]] = String(checkedInput.value).trim();
        });

        return selectedByPosition;
      }

      getSelectedOptionsInOrder(variantSelects) {
        const selectedOptions = [];

        Array.from(variantSelects.children).forEach((child) => {
          if (child.tagName === "FIELDSET") {
            const checkedInput = child.querySelector('input[type="radio"]:checked');
            if (checkedInput?.value) selectedOptions.push(String(checkedInput.value).trim());
            return;
          }

          const select = child.querySelector?.("select");
          if (select?.value) selectedOptions.push(String(select.value).trim());
        });

        return selectedOptions;
      }

      getOptionValueIdsFromVariantSelects(variantSelects) {
        const optionValueIds = [];

        variantSelects.querySelectorAll("select").forEach((select) => {
          const selectedOption = select.selectedOptions?.[0];
          const optionValueId = selectedOption?.dataset?.optionValueId;
          if (optionValueId) optionValueIds.push(optionValueId);
        });

        variantSelects.querySelectorAll("fieldset").forEach((fieldset) => {
          const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
          const optionValueId = checkedInput?.dataset?.optionValueId;
          if (optionValueId) optionValueIds.push(optionValueId);
        });

        return optionValueIds;
      }

      isColorSelectionChange(target) {
        if (!target) return false;

        const fieldset = target.closest?.("fieldset");
        if (fieldset) {
          return !!fieldset.querySelector(".product-option__swatch");
        }

        const inputWrapper = target.closest?.(".product-form__input");
        if (!inputWrapper) return false;

        return !!inputWrapper.querySelector(".dropdown-swatch, [data-selected-value] .product-option__swatch");
      }

      getAutoSelectedFirstAvailableSizePayload(html) {
        const variantSelects = html.querySelector("product-variant-selects");
        if (!variantSelects) return null;

        const sizeFieldset = Array.from(variantSelects.querySelectorAll("fieldset")).find(
          (fieldset) => !fieldset.querySelector(".product-option__swatch")
        );
        if (!sizeFieldset) return null;

        const firstAvailableSize = sizeFieldset.querySelector('input[type="radio"]:not([disabled]):not(.disabled):not([data-soldout="true"])');
        if (!firstAvailableSize) return null;

        const checkedSize = sizeFieldset.querySelector('input[type="radio"]:checked');
        if (checkedSize === firstAvailableSize) return null;

        if (checkedSize) checkedSize.checked = false;
        firstAvailableSize.checked = true;

        const optionValueIds = this.getOptionValueIdsFromVariantSelects(variantSelects);
        if (!optionValueIds.length) return null;
        const resolvedVariant = this.getVariantFromSelectedOptions(html);

        return {
          optionValueIds,
          variantId: resolvedVariant?.id || null,
          targetId: firstAvailableSize.id,
        };
      }

      isSelectedSizeUnavailable(productInfoNode = this) {
        const variantSelects = productInfoNode?.querySelector("product-variant-selects");
        if (!variantSelects) return false;

        const nonColorFieldsets = variantSelects.querySelectorAll("fieldset");
        for (const fieldset of nonColorFieldsets) {
          if (fieldset.querySelector(".product-option__swatch")) continue;
          const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
          if (!checkedInput) continue;
          if (checkedInput.hasAttribute("disabled") || checkedInput.classList.contains("disabled") || checkedInput.dataset.soldout === "true") {
            return true;
          }
        }
        return false;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false,
        variantId = null
      ) {
        const requestUrl = new URL(url, window.location.origin);

        if (!shouldFetchFullPage) {
          requestUrl.searchParams.set("section_id", this.sectionId);
        } else {
          requestUrl.searchParams.delete("section_id");
        }

        if (variantId) {
          requestUrl.searchParams.set("variant", variantId);
          requestUrl.searchParams.delete("option_values");
        } else if (optionValues.length) {
          requestUrl.searchParams.set("option_values", optionValues.join(","));
          requestUrl.searchParams.delete("variant");
        }

        return `${requestUrl.pathname}${requestUrl.search}`;
      }

      updateOptionValues(html) {
        const productVariantSelects = html.querySelector("product-variant-selects");
        if (productVariantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            productVariantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl, isColorChange = false, hasAutoSelectedSize = false) {
        return html => {
          if (isColorChange && !hasAutoSelectedSize) {
            const autoSelectedPayload = this.getAutoSelectedFirstAvailableSizePayload(html);
            if (autoSelectedPayload) {
              this.renderProductInfo({
                requestUrl: this.buildRequestUrlWithParams(
                  productUrl,
                  autoSelectedPayload.optionValueIds,
                  false,
                  autoSelectedPayload.variantId
                ),
                targetId: autoSelectedPayload.targetId,
                callback: this.handleUpdateProductInfo(productUrl, true, true),
              });
              return;
            }

            const variantFromOptions = this.getVariantFromSelectedOptions(html);
            const payloadSelectedVariant = this.getPayloadSelectedVariant(html);
            if (variantFromOptions?.id && payloadSelectedVariant?.id !== variantFromOptions.id) {
              const focusTarget =
                html.querySelector("product-variant-selects input[type='radio']:checked, product-variant-selects select option:checked");
              this.renderProductInfo({
                requestUrl: this.buildRequestUrlWithParams(
                  productUrl,
                  [],
                  false,
                  variantFromOptions.id
                ),
                targetId: focusTarget?.id || this.variantSelectors?.id,
                callback: this.handleUpdateProductInfo(productUrl, true, true),
              });
              return;
            }
          }

          const variant = this.getSelectedVariant(html);
          const selectedSizeUnavailable = this.isSelectedSizeUnavailable(html);

          // this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantDiscountBadge(variant?.price, variant?.compare_at_price);
          this.updateVariantDetails(variant?.id);
          // Inserted logic for pickupAvailability after updateVariantDetails
          if (this.pickupAvailability && variant?.id) {
            this.pickupAvailability.dataset.variantId = variant.id;
            this.pickupAvailability.fetchAvailability(variant.id);
          }

          if (
            isColorChange &&
            this.productMediaGallery &&
            this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true'
          ) {
            try {
              this.updateVariantMedia(html, variant);
            } catch (error) {
              console.error('Variant media update failed:', error);
            }
          }

          // Always update these from server response, even when variant is null
          const updateSourceFromDestination = (
            id,
            shouldHide = source => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {

              destination.innerHTML = source.innerHTML;

              if (id === 'Inventory') {
                const inventoryThreshold = parseInt(source.dataset.inventoryThreshold);
                const inventoryQuantity = parseInt(source.dataset.inventoryQuantity);
                // twins (custom.size_alternatives): the notice stays visible for a sold-out variant, saying "Out of stock"
                const showSoldOut = source.dataset.showSoldOut === "true";
                destination.classList.toggle("hidden", !showSoldOut && (inventoryThreshold && inventoryQuantity > inventoryThreshold || inventoryQuantity < 1));
              } else {
                destination.classList.toggle("hidden", shouldHide(source));
              }
            }
          };

          updateSourceFromDestination("ProductPurchaseInfo");
          updateSourceFromDestination("ProductStickyCart");
          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"));
          updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "");


          if (!variant) {
            this.updateVariantInputs(null);
            this.setUnavailable();
            this.syncStickyCartVariant(null, true);
            return;
          }

          const sourceSubmitButton =
            html.getElementById(`ProductSubmitButton-${this.sectionId}`) ||
            html.querySelector('[id^="ProductSubmitButton-"]');
          const shouldDisableSubmitButton = selectedSizeUnavailable || (variant
            ? !variant.available
            : sourceSubmitButton?.hasAttribute("disabled") ?? true);
          this.productForm?.toggleSubmitButton(
            shouldDisableSubmitButton,
            window.variantStrings.soldOut
          );
          this.updateVariantInputs(shouldDisableSubmitButton ? null : variant?.id);
          if (!shouldDisableSubmitButton) {
            this.productForm?.submitButton?.removeAttribute("aria-disabled");
          }
          this.syncStickyCartVariant(variant, shouldDisableSubmitButton);

          if (isColorChange) {
            this.updateMedia(variant?.featured_media?.id);
          }

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");


          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant
            }
          });
        };
      }

      getVariantColorBoundaries() {
        const allVariantsEl = this.querySelector('product-variant-selects [data-all-variants]');
        if (!allVariantsEl) return null;

        let allVariants;
        try {
          allVariants = JSON.parse(allVariantsEl.textContent);
        } catch (e) {
          return null;
        }

        const colorBoundaries = [];
        const seenColors = new Set();
        for (const variant of allVariants) {
          const color = this.getVariantColorValue(variant);
          if (color && variant.featured_media && !seenColors.has(color)) {
            seenColors.add(color);
            colorBoundaries.push({ color, mediaId: variant.featured_media.id });
          }
        }
        return colorBoundaries;
      }

      getColorOptionPosition() {
        const positionFromGallery = this.productMediaGallery?.dataset?.colorOptionPosition;
        const parsedPosition = parseInt(positionFromGallery, 10);
        return Number.isNaN(parsedPosition) ? 1 : parsedPosition;
      }

      getVariantColorValue(variant) {
        if (!variant) return null;

        const colorOptionPosition = this.getColorOptionPosition();
        const colorOptionKey = `option${colorOptionPosition}`;
        const colorValueByKey = variant[colorOptionKey];
        if (colorValueByKey) {
          return colorValueByKey.toLowerCase().trim();
        }

        const colorValueByOptionsArray = variant.options?.[colorOptionPosition - 1];
        if (colorValueByOptionsArray) {
          return colorValueByOptionsArray.toLowerCase().trim();
        }

        if (variant.option1) {
          return variant.option1.toLowerCase().trim();
        }

        return null;
      }

      normalizeColorValue(colorValue) {
        const normalized = String(colorValue ?? "")
          .toLowerCase()
          .replace(/_/g, " ")
          .replace(/-/g, " ")
          .trim();

        const withoutDiacritics = normalized.normalize
          ? normalized.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
          : normalized;

        return withoutDiacritics.replace(/\s+/g, " ").trim();
      }

      extractColorTagValueFromAlt(altText) {
        const normalizedAlt = String(altText ?? "").toLowerCase();
        const colorTag = "#color_";
        const colorTagIndex = normalizedAlt.indexOf(colorTag);
        if (colorTagIndex === -1) return null;

        const afterColorTag = normalizedAlt.slice(colorTagIndex + colorTag.length);
        const colorTagValue = afterColorTag.split("#")[0];
        const normalizedColor = this.normalizeColorValue(colorTagValue);

        return normalizedColor || null;
      }

      updateVariantMedia(html, variant) {
        if (!html) return;

        // Replace the entire media gallery with the server-rendered version
        const newGallery = html.getElementById(`MediaGallery-${this.sectionId}`);
        if (!newGallery || !this.productMediaGallery) return;

        this.productMediaGallery.innerHTML = newGallery.innerHTML;

        // Copy attributes
        Array.from(newGallery.attributes).forEach(attr => {
          this.productMediaGallery.setAttribute(attr.name, attr.value);
        });

        // Client-side color filtering fallback
        // Server-side Liquid filter may not work during section re-renders,
        // so we filter slides here based on #color_ alt text tags
        const selectedColor = this.extractSelectedColor(variant);
        if (selectedColor) {
          this.filterSlidesByColor(this.productMediaGallery, selectedColor);
        }

        // Re-initialize swiper instances for the new gallery content
        const newMainGallery = this.productMediaGallery.querySelector('swiper-product-gallery');
        const newThumbsGallery = this.productMediaGallery.querySelector('swiper-product-thumbs');

        if (newThumbsGallery) {
          newThumbsGallery.swiperInitialized = false;
          newThumbsGallery.init();
        }
        if (newMainGallery) {
          newMainGallery.swiperInitialized = false;
          newMainGallery.init();
        }

        // Also replace pagination element if it exists
        const currentPagination = this.productMediaGallery.parentElement?.querySelector('.swiper--product-gallery__pagination');
        const newPagination = html.querySelector('.swiper--product-gallery__pagination');
        if (currentPagination && newPagination) {
          currentPagination.innerHTML = newPagination.innerHTML;
        }
      }

      extractSelectedColor(variant) {
        // 1) The gallery wrapper carrying the variant's featured media exposes
        //    its colour as data-media-color (snippets/media-alt-attrs.liquid).
        const featuredMediaId = variant?.featured_media?.id;
        if (featuredMediaId && this.productMediaGallery) {
          const featuredSlide = this.productMediaGallery.querySelector(`[data-media-id="${featuredMediaId}"][data-media-color]`);
          const slideColor = this.normalizeColorValue(featuredSlide?.dataset.mediaColor || '');
          if (slideColor) return slideColor;
        }
        // 2) Fallback: the '#color_' tag in the variant JSON's media alt (the
        //    JSON keeps the raw admin alt; the rendered DOM does not).
        if (variant?.featured_media?.alt) {
          const mediaAltColor = this.extractColorTagValueFromAlt(variant.featured_media.alt);
          if (mediaAltColor) return mediaAltColor;
        }
        // 3) Fallback: detected color option (not always option1)
        return this.normalizeColorValue(this.getVariantColorValue(variant));
      }

      filterSlidesByColor(gallery, selectedColor) {
        const normalizedSelectedColor = this.normalizeColorValue(selectedColor);
        if (!normalizedSelectedColor) return;

        // Filter main gallery, thumbs, and grid slides
        const slideContainers = gallery.querySelectorAll(
          'swiper-product-gallery .swiper-slide, swiper-product-thumbs .swiper-slide, .main-product__media--grid-item'
        );

        slideContainers.forEach(slide => {
          // Structured attributes written by Liquid — data-media-color on the
          // wrapper (snippets/media-alt-attrs.liquid), data-color on the <img>
          // (snippets/image.liquid); the raw '#color_' alt tag never reaches the DOM.
          const slideImg = slide.querySelector('img');
          const mediaColor = this.normalizeColorValue(slide.dataset.mediaColor || slideImg?.dataset.color || '') || null;
          if (mediaColor) {
            if (mediaColor !== normalizedSelectedColor && mediaColor !== 'all') {
              slide.remove();
            }
          } else {
            // No #color_ tag = not tagged for any color, remove it
            slide.remove();
          }
        });
      }

      updateVariantOptionMedia() {
        this.productMediaGallery.querySelectorAll('[data-media-group]:not(.hidden)').forEach(element => {

        });
      }

      updateVariantInputs(variantId) {
        const formSelector = [
          `#product-form-${this.dataset.section}`,
          `#product-form-${this.sectionId}`,
          `#product-form-installment-${this.dataset.section}`,
          `#product-form-installment-${this.sectionId}`,
          'form[data-type="add-to-cart-form"]',
          'form.installment',
        ].join(', ');

        this.querySelectorAll(formSelector).forEach(productForm => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? "";
          if (variantId) {
            input.removeAttribute("disabled");
          } else {
            input.setAttribute("disabled", "disabled");
          }
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const urlWithVariant = this.getUrlWithVariant(url, variantId);

        // Update share-button URL so sharing the page sends the picked variant.
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${urlWithVariant}`
        );

        // Reflect the selected variant in the address bar (?variant=ID),
        // preserving any other query params (e.g. selling_plan). The canonical
        // <link> tag is rendered server-side and is unaffected by replaceState.
        const params = new URLSearchParams(window.location.search);
        if (variantId) {
          params.set("variant", variantId);
        } else {
          params.delete("variant");
        }
        const qs = params.toString();
        // Keep a trailing slash on the product path so the address bar matches the
        // server-rendered canonical tag, the share URL, and the collection card links.
        const basePath = this.dataset.url.endsWith("/")
          ? this.dataset.url
          : `${this.dataset.url}/`;
        window.history.replaceState({}, "", qs ? `${basePath}?${qs}` : basePath);
      }

      updateSellingPlanURL(sellingPlanId, selectedVariantId) {
        if (!selectedVariantId) return;
        if (!sellingPlanId) {
          window.history.replaceState({}, "", `${window.location.href.replace(/&selling_plan=\d+/, "")}`);
        } else {
          const params = new URLSearchParams(window.location.search);
          params[params.has("variant") ? "set" : "append"](
            "variant",
            selectedVariantId
          );
          if (sellingPlanId) {
            params[params.has("selling_plan") ? "set" : "append"](
              "selling_plan",
              sellingPlanId
            );
          } else {
            params.delete("selling_plan");
          }
          window.history.replaceState({}, "", `${this.dataset.url}?${params.toString()}`);
        }
      }

      updateVariantDetails(variantId) {
        const productVariantDetails = this.querySelectorAll('.product__metafield_for_variant-block');
        if (productVariantDetails) {
          productVariantDetails.forEach((productVariantDetail) => {
            const block_id = productVariantDetail.getAttribute('data-block-id');
            const metafieldDataElement = this.querySelector(`#product__metafield_for_variant-block--${block_id}`);
            if (!metafieldDataElement) {
              return;
            }
            try {
              let productVariantDetailsData = JSON.parse(metafieldDataElement?.textContent);
              productVariantDetail.innerHTML = productVariantDetailsData?.[variantId]?.replaceAll('\n', '<br />') || '';
            } catch (error) {
              console.error('Error parsing JSON', error);
            }
          });
        }
      }

      updateVariantDiscountBadge(price, compare_at_price) {
        const discountBadge = this.querySelector(".product-card__badge--discount");
        if (!discountBadge) return;


        if (compare_at_price > price) {
          discountBadge.classList.remove("hidden");
          const percentageElement = discountBadge.querySelector(".percentage");

          if (!percentageElement) return;
          // calculate discount percentage
          const discountPercentage = Math.round(
            ((compare_at_price - price) / compare_at_price) * 100
          );
          // update discount percentage
          percentageElement.textContent = discountPercentage;
        } else {
          discountBadge.classList.add("hidden");
        }
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Quantity-Rules"
        ]
          .map(id => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      syncStickyCartVariant(variant, shouldDisableSubmitButton = false) {
        const stickyCart =
          this.querySelector(`#ProductStickyCart-${this.dataset.section}`) ||
          this.querySelector(`#ProductStickyCart-${this.sectionId}`);
        if (!stickyCart) return;

        const stickyButton = stickyCart.querySelector(".product-bar__submit, button[type='submit']");
        if (stickyButton) {
          const mainFormId = this.productForm?.form?.id;
          if (mainFormId) stickyButton.setAttribute("form", mainFormId);
          stickyButton.toggleAttribute("disabled", !!shouldDisableSubmitButton);

          if (!stickyButton.dataset.submitBound) {
            stickyButton.addEventListener("click", (event) => {
              event.preventDefault();
              if (stickyButton.hasAttribute("disabled")) return;

              const form = this.productForm?.form;
              const submitter = this.productForm?.submitButton;
              if (!form) return;

              if (submitter && !submitter.hasAttribute("disabled")) {
                form.requestSubmit(submitter);
              } else {
                form.requestSubmit();
              }
            });
            stickyButton.dataset.submitBound = "true";
          }

          const stickyButtonText = stickyButton.querySelector("span:last-child") || stickyButton.querySelector("span");
          const mainButtonText = this.productForm?.submitButton?.querySelector("span")?.textContent?.trim();
          if (stickyButtonText && mainButtonText) stickyButtonText.textContent = mainButtonText;
        }

        if (!variant) return;
        this.syncStickyCartImage(stickyCart, variant);

        const stickyOptionLabels = stickyCart.querySelectorAll(".product__sticky-cart--opt-label");
        if (stickyOptionLabels.length) {
          stickyOptionLabels.forEach((label, index) => {
            const optionPosition = parseInt(label.dataset.optionPosition, 10);
            if (!Number.isNaN(optionPosition) && optionPosition > 0) {
              const optionValue =
                variant?.[`option${optionPosition}`] ??
                variant?.options?.[optionPosition - 1];
              if (optionValue) label.textContent = optionValue;
              return;
            }

            if (Array.isArray(variant.options) && variant.options[index]) {
              label.textContent = variant.options[index];
            }
          });
        }

        const stickyPrice = stickyCart.querySelector(".product__sticky-cart--price");
        const livePrice =
          this.querySelector(`#price-${this.dataset.section} .price`) ||
          this.querySelector(`#price-${this.sectionId} .price`) ||
          this.querySelector('[id^="price-"] .price');
        if (stickyPrice && livePrice) {
          stickyPrice.innerHTML = livePrice.innerHTML;
        }
      }

      syncStickyCartImage(stickyCart, variant) {
        const stickyImage = stickyCart.querySelector(".product__sticky-cart--info .media img");
        const mediaId = variant?.featured_media?.id;
        if (!stickyImage || !mediaId) return;

        const variantImage =
          this.querySelector(`swiper-product-gallery .swiper-slide[data-media-id="${mediaId}"] img`) ||
          this.querySelector(`.main-product__media--grid .swiper-slide[data-media-id="${mediaId}"] img`) ||
          this.querySelector(`[data-media-id="${mediaId}"] img`);

        if (variantImage) {
          ["src", "srcset", "sizes", "alt", "data-color", "data-ai-image", "width", "height"].forEach((attribute) => {
            const value = variantImage.getAttribute(attribute);
            if (value) {
              stickyImage.setAttribute(attribute, value);
            } else {
              stickyImage.removeAttribute(attribute);
            }
          });
          return;
        }

        const fallbackSrc = variant?.featured_media?.preview_image?.src || variant?.featured_media?.src;
        if (fallbackSrc) {
          stickyImage.setAttribute("src", fallbackSrc);
          // Keep the public alt free of internal '#…' tags (mirrors
          // snippets/image.liquid); expose them as data-color / data-ai-image.
          const rawAlt = String(variant?.featured_media?.alt || "");
          stickyImage.setAttribute("alt", rawAlt.replace(/(?:^|\s)#\S+/g, " ").replace(/\s+/g, " ").trim());
          const colorTagMatch = rawAlt.toLowerCase().match(/#color_([^\s#]+)/);
          if (colorTagMatch) {
            stickyImage.setAttribute("data-color", colorTagMatch[1]);
          } else {
            stickyImage.removeAttribute("data-color");
          }
          if (/#ai-image/i.test(rawAlt)) {
            stickyImage.setAttribute("data-ai-image", "true");
          } else {
            stickyImage.removeAttribute("data-ai-image");
          }
        }
      }

      updateMedia(variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        // if layout is grid, add .active class to the relevant swiper-slide
        const gridLayout = this.querySelector('.main-product__media--grid');
        if (gridLayout) {
          // remove active class from all swiper-slides
          gridLayout.querySelectorAll('.swiper-slide').forEach(item => {
            item.classList.remove('active');
          });
          // add .active class to the swiper-slide with the matching data-media-id
          const activeSlide = gridLayout.querySelector(`.swiper-slide[data-media-id="${variantFeaturedMediaId}"]`);
          if (activeSlide) {
            activeSlide.classList.add('active');
          }
        }

        if (
          this.querySelector(".main-product__media")?.hasAttribute(
            "data-desktop-grid"
          ) && !DeviceDetector.isMobile()
        ) {
          this.scrollToImage(variantFeaturedMediaId);
        }

        const productMedia = this.querySelector(
          "swiper-product-gallery"
        );
        productMedia?.setActiveMedia(variantFeaturedMediaId);
      }

      scrollToImage(variantFeaturedMediaId) {
        this.querySelectorAll("[data-media-id]").forEach(img => {
          if (
            img.getAttribute("data-media-id") ==
            variantFeaturedMediaId
          ) {
            img.scrollIntoView({
              block: "end",
              behavior: "smooth"
            });
          }
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step
            ? parseInt(this.quantityInput.step)
            : 1
        };

        let min = data.min;
        const max =
          data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min)
          min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId =
          this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then(response => response.text())
          .then(responseText => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch(e => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput || !this.quantityForm) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        if (!quantityFormUpdated) return;
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label"
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step"
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(
          `#ProductModal-${this.dataset.section}`
        );
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("product-variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      getProductPath(url) {
        if (!url) return "";
        try {
          return new URL(url, window.location.origin).pathname;
        } catch (error) {
          return String(url).split("?")[0];
        }
      }

      getUrlWithVariant(url, variantId) {
        const resolvedUrl = new URL(url, window.location.origin);
        if (!resolvedUrl.pathname.endsWith('/') && !resolvedUrl.pathname.includes('.')) {
          resolvedUrl.pathname += '/';
        }
        if (variantId) {
          resolvedUrl.searchParams.set("variant", variantId);
        } else {
          resolvedUrl.searchParams.delete("variant");
        }
        return `${resolvedUrl.pathname}${resolvedUrl.search}`;
      }
    }
  );
}

if (!customElements.get('product-form')) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener(
          "submit",
          this.onSubmitHandler.bind(this)
        );
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        const productInfo = this.closest("product-info");
        const selectedSizeUnavailable = productInfo?.isSelectedSizeUnavailable?.(productInfo);
        if (selectedSizeUnavailable) {
          this.submitButton.setAttribute("disabled", "disabled");
          this.submitButton.setAttribute("aria-disabled", "true");
          return;
        }

        const selectedVariant = productInfo?.getSelectedVariant?.(productInfo);
        if (selectedVariant?.id) {
          this.variantIdInput.value = selectedVariant.id;
          this.variantIdInput.disabled = false;
          if (selectedVariant.available) {
            this.submitButton.removeAttribute("disabled");
            this.submitButton.removeAttribute("aria-disabled");
          }
        }

        if (
          this.submitButton.classList.contains("loading") ||
          this.submitButton.hasAttribute("disabled")
        ) {
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove(
          "hidden"
        );

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);

        if (this.hasAttribute('data-has-pre-order')) {
          // Захиалгад ҮРГЭЛЖ англиар хадгална — EU нь олон хэлээр ажилладаг тул
          // орчуулсан түлхүүр захиалгуудыг хэл бүрээр өөр болгож, ops талд шүүхэд
          // төвөгтэй болгоно. Харуулах орчуулгыг line-item.liquid хийнэ —
          // тэнд ЭНЭ ижил тогтмол мөрөөр тулгадаг тул хамт өөрчлөх ёстой.
          formData.append('properties[Pre-order]', '✓');
        }
        if (this.cart) {
          formData.append(
            "sections",
            this.cart
              .getSectionsToRender()
              .map(section => section.section)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(response => response.json())
          .then(response => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector(
                ".sold-out-message"
              );
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response
              });
            this.error = false;
            this.cart.renderContents(response);
          })
          .catch(e => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error)
              this.submitButton.removeAttribute("aria-disabled");
            if (!this.submitButton.hasAttribute("disabled")) {
              this.submitButton.removeAttribute("aria-disabled");
            }
            this.querySelector(".loading__spinner").classList.add(
              "hidden"
            );
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute(
          "hidden",
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = this.hasAttribute(
            "data-has-selling-plan"
          )
            ? window.variantStrings.addSubscriptionToCart
            : this.submitButtonText.textContent = this.hasAttribute(
                "data-has-pre-order"
              )
            ?  window.variantStrings.preOrder
            : window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}

document.querySelectorAll('.product__content > *').forEach((el) => {
  if (el.classList.contains('product__pickup-availabilities')  || el.classList.contains('product__block--custom-liquid') || el.classList.contains('product__app-block')) return;
  if (!el.textContent.trim()) {
    el.textContent = '';
  }
});
